import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, signal, computed, OnInit, QueryList, ViewChildren, effect, inject, ElementRef, ViewChild, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IDataService } from '../../services/IDataService';
import { IDetailComponent } from '../_primitives/IDetailComponent';
import { IEntityMetaData, IMetaProprety, IPropretyAttribute } from '../../_models/IEntityMetaData';
import { EntityBase } from '../../_models/EntityBase';
import { GenericComponent } from '../_base/GenericComponent';
import { TranslationService } from '../../services/TranslationService';
import { GenericValidationService } from '../../services/GenericValidationService';
import { ValidationState } from '../../_models/IValidationError';
import { NgZone } from '@angular/core';
import { AlertModalService } from '../../services/AlertModalService';
import { OnReadyDirective } from '../directives/OnReadyDirective';
import { LookupItem, LookupService } from '../../services/LookupService';
import { SiteTimeZoneService } from '../../services/SiteTimeZoneService';
import { PermissionsService } from '../../services/Permissions.service';
import { ComponentInstancesService } from '../../services/componentInstances.service';
import { DependencyInjectionProvider } from '../../services/DependecyInjection.provider';
import { EntityState } from '../../_models/IChangedEntity';
import { AlertModalResult } from '../nested-components/alert-dialog/alert-dialog.component';
import { LoaderService } from '../../services/loader.service';
import { MatwinsVariablesService } from '../../services/matwinsVariables.service';
import { IFieldHost } from '../../_models/IFieldHost';

@Component({
  template: '', // fourni par les enfants
})
export abstract class GenetricDetailComponent<T extends EntityBase> extends GenericComponent<T>
  implements IDetailComponent<T>, IFieldHost, OnInit, OnChanges {

  protected currentOpenModalId: string | null = null;


  public ComponentEntityClass: any;
  public entityMetaData: IEntityMetaData = this.getEmptyEntity().GetType();
  public Editable: boolean = true;

  @Input() EntityInstance: T | null = null;
  @Input() dataService!: IDataService<T>;
  @Input() isModal = false;
  @Input() resumeUrl!: string;
  @Output() entityUpdated = new EventEmitter<T>();
  @Output() closeModal = new EventEmitter<boolean>();

  @ViewChildren(OnReadyDirective) onReadyDirectives!: QueryList<OnReadyDirective>;

  public currentImage: File | null = null;
  public imageUrl: string | null = null;
  protected imageRelationName = 'FK_Image_Photo';

  @ViewChild('fileInput') protected fileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('scrollContainer', { static: false }) scrollContainer?: ElementRef;


  public matwinsVariables = inject(MatwinsVariablesService);

  public isEditMode = signal(false);
  public editingEntity = signal<T | null>(null);
  public isNew = signal(false);
  public validationErrors = signal<ValidationState>({});
  public isFormValid = computed(() => {
    let errors = this.validationErrors();
    return Object.keys(errors).length === 0 || Object.values(errors).every(errArray => errArray.length === 0);
  });

  public generalErrorCount = computed(() => {
    const errors = this.validationErrors();
    return Object.values(errors).reduce((total, errArray) => total + errArray.length, 0);
  });

  protected viewModelProperties: string[] = [];
  public alertModalService?: AlertModalService;
  protected lookupService: LookupService = inject(LookupService); // Injected LookupService
  protected siteTimeZoneService: SiteTimeZoneService = inject(SiteTimeZoneService);

  // Fuseau du site edite, s'il differe du lecteur : les champs affichent l'heure du LECTEUR.
  protected get fuseauDuSite(): string | null {
    return this.siteTimeZoneService.fuseauDuSite((this.editingEntity() as any)?.fK_PointOfService);
  }

  public permissionsService: PermissionsService = inject(PermissionsService);

  protected di = inject(DependencyInjectionProvider);

  public loaderService: LoaderService = inject(LoaderService);


  private refreshTrigger = signal(0);
  public flashingProperty = signal<string | null>(null);

  componentInstancesService: ComponentInstancesService = inject(ComponentInstancesService);


  Object = Object;

  constructor(
    protected route: ActivatedRoute,
    protected router: Router,
    public translationService: TranslationService,
    private validationService: GenericValidationService,
    protected ngZone: NgZone
  ) {



    super();
    this.translationService = GenericComponent.getTranslationService();
    this.componentInstancesService.addComponentInstance(this.getComponentId(), this);
    // Réagit au changement de l'entité
    effect(() => {
      const entity = this.editingEntity();

      setTimeout(() => {
        this.onReadyDirectives?.forEach(directive => {
          (directive as any).onReadyTrigger = entity;
        });
      });

      this.resetImage();
      this.imageUrl = entity?.['imagePhoto']?.rawData_Path ? 'Documents/' + entity?.['imagePhoto']?.rawData_Path : null;

    });
  }

  public override get RequiredPermissions(): string[] {

    let permissions = [];
    let namespace = this.entityMetaData.Namespace ? `${this.entityMetaData.Namespace}.` : '';
    permissions.push(`Permissions.Entities.${namespace}${this.entityMetaData.EntityName}.Read`);

    return permissions;
  }

  public override get HasRequiredPermissions(): boolean {
    return this.permissionsService.UserHasAllPermissions(this.RequiredPermissions);
  }

  public get UpdateMode(): string {
    let disableClass = 'pointer-events-none select-none opacity-90';

    return ((this.isEditMode() && !this.HasUpdatePermission) || (this.isNew() && !this.HasCreatePermission)) ? disableClass : '';
  }

  public get HasEditPermission(): boolean {
    return this.HasUpdatePermission || this.HasCreatePermission;
  }

  public get HasCreatePermission(): boolean {
    let namespace = this.entityMetaData.Namespace ? `${this.entityMetaData.Namespace}.` : '';
    return this.permissionsService.UserHasSinglePermission(`Permissions.Entities.${namespace}${this.entityMetaData.EntityName}.Create`);
  }

  public get HasUpdatePermission(): boolean {
    let namespace = this.entityMetaData.Namespace ? `${this.entityMetaData.Namespace}.` : '';
    return this.permissionsService.UserHasSinglePermission(`Permissions.Entities.${namespace}${this.entityMetaData.EntityName}.Update`);
  }

  public getLookupData(lookupName: string, lookupNamespace?: string, additionalDataFiledName: string | null = null, instanceFilterPropName: string | null = null): any[] {

    let fullLookup = this.lookupService.getLookupData(lookupName, lookupNamespace);

    if (additionalDataFiledName && additionalDataFiledName.length > 0 && instanceFilterPropName) {
      let currentInstance = this.EntityInstance || {};
      if (instanceFilterPropName in currentInstance) {
        let value = currentInstance[instanceFilterPropName as keyof typeof currentInstance];
        let filteredLookup = fullLookup.filter((lookupItem: LookupItem, index: number) => {
          return (lookupItem.additionalData ? lookupItem.additionalData[additionalDataFiledName] == value : false);
        });
        return filteredLookup;
      }
    }
    return fullLookup;
  }

  protected openModal(modalId: string): void {
    const modal = document.getElementById(modalId);
    if (modal) {
      modal.classList.remove('hidden');
      this.currentOpenModalId = modalId;
    }
  }

  protected hideModal(modalId: string): void {
    const modal = document.getElementById(modalId);
    if (modal) {
      modal.classList.add('hidden');
      if (this.currentOpenModalId === modalId) {
        this.currentOpenModalId = null;
      }
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKeydown(event: Event): void {
    // Priorité 1: Modals HTML trackés
    if (this.currentOpenModalId) {
      this.hideModal(this.currentOpenModalId);
      event.preventDefault();
      return;
    }

    // Priorité 2: DetailComponent en mode modal
    if (this.isModal && this.isEditMode()) {
      this.cancelEdit();
      event.preventDefault();
      return;
    }

    // Priorité 3: Détection automatique DOM
    const openModals = document.querySelectorAll('.fixed:not(.hidden)');
    if (openModals.length > 0) {
      const lastModal = openModals[openModals.length - 1] as HTMLElement;
      if (lastModal.id && this.hideModal) {
        this.hideModal(lastModal.id);
        event.preventDefault();
      }
    }
  }

  private scrollToTop(): void {
    if (this.scrollContainer?.nativeElement) {
      this.scrollContainer.nativeElement.scrollTop = 0;
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }


  protected updateEntityProperty2<K extends keyof T>(
    propertyName: K,
    newValue: T[K]
  ): void {
    this.editingEntity.update(entity => {
      if (entity) {
        entity[propertyName] = newValue;
      }
      return entity;
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.dataService = this.di.getOrCreateDataService(this.ComponentEntityClass);

    if (this.isModal) {
      if (!this.EntityInstance) {
        this.isNew.set(true);
        this.editingEntity.set(this.getEmptyEntity());
      } else {
        this.isNew.set(false);
        this.editingEntity.set(Object.assign(this.getEmptyEntity(), this.EntityInstance));
        // this.editingEntity.set(this.EntityInstance);
      }
      this.isEditMode.set(true);
    } else {
      this.route.data.subscribe(data => {
        const mode = data['mode'];
        const id = this.route.snapshot.paramMap.get('id');
        if (id) {
          this.loadData(+id);
        }
        if (mode === 'add') {
          this.isNew.set(true);
          this.isEditMode.set(true);
          this.editingEntity.set(this.getEmptyEntity());
        } else if (mode === 'edit') {
          this.isEditMode.set(true);
        }
      });
    }
  }

  override ngOnChanges(changes: SimpleChanges): void {
    if (changes['EntityInstance'] && !changes['EntityInstance'].firstChange && changes['EntityInstance'].currentValue !== null) {
      this.enterEditMode();
    }

    // Toujours synchroniser editingEntity avec EntityInstance
    this.editingEntity.set(Object.assign(this.getEmptyEntity(), this.EntityInstance));
    if (!this.isEditMode()) {
      this.enterEditMode();
    }

  }

  async loadData(id: number): Promise<void> {
    try {
      this.EntityInstance = Object.assign(this.getEmptyEntity(), await this.dataService.get(id));
      if (this.isEditMode()) {
        this.editingEntity.set(Object.assign(this.getEmptyEntity(), this.EntityInstance));
        // this.editingEntity.set(this.EntityInstance);
      }
      setTimeout(() => this.scrollToTop(), 0);

    } catch (error) {
      console.error('Failed to load data', error);
    }
  }

  override getComponentId(): string {
    if (this.Initiator) {
      return this.Initiator.getComponentId();
    }
    return this.componentId;
  }

  enterEditMode(): void {
    this.validationErrors.set({});
    if (this.EntityInstance && this.EntityInstance.id > 0) {
      this.isNew.set(false);
      this.editingEntity.set(Object.assign(this.getEmptyEntity(), this.EntityInstance));
      // this.editingEntity.set(this.EntityInstance);
      this.isEditMode.set(true);
    }
    else {
      this.isNew.set(true);
      this.editingEntity.set(this.EntityInstance);
    }
    setTimeout(() => this.scrollToTop(), 0);

  }

  cancelEdit(): void {
    if (this.isModal) {
      this.closeModal.emit(false);
    } else if (this.isNew()) {
      this.router.navigate([this.resumeUrl]);
    } else {
      this.isEditMode.set(false);
    }
  }

  private getPropertyDescriptor(propertyName: string): IMetaProprety | null {
    const propertyParts = propertyName.split('.');
    let currentMetaData: IEntityMetaData = this.entityMetaData;
    let propertyDescriptor: IMetaProprety | null = null;

    for (let i = 0; i < propertyParts.length; i++) {
      const part = propertyParts[i];
      propertyDescriptor = currentMetaData.PropretyDescriptors[part];

      if (propertyDescriptor && propertyDescriptor.TargetTypeMetaData && i < propertyParts.length - 1) {
        currentMetaData = propertyDescriptor.TargetTypeMetaData;
      } else if (i < propertyParts.length - 1) {
        return null; // Intermediate property not found or not a nested property
      }
    }

    return propertyDescriptor;
  }

  validateProperty(propertyName: string, value: any): void {
    const propertyDescriptor = this.getPropertyDescriptor(propertyName as string);

    if (!propertyDescriptor) {
      console.warn(`Aucun descriptor trouvé pour la propriété: ${String(propertyName)}`);
      return;
    }

    const validationResult = this.validationService.validateProperty(propertyName as string, value, propertyDescriptor, this.editingEntity(), this.isNew());

    this.validationErrors.update(errors => ({
      ...errors,
      [propertyName as string]: validationResult.isValid ? [] : validationResult.errors.map(e => e.message)
    }));
  }

  //

  bindingHandler(inputElement: HTMLElement, fieldName: string, componentInstance?: any, eventName: string = 'selectedKeysChange'): void {
    const element = inputElement as HTMLInputElement | HTMLSelectElement;
    const model = this.editingEntity();



    if (model) {
      const value = this.getPropertyValue(model, fieldName);

      // Détection du type d'élément
      if (element.tagName === 'SELECT') {
        // Pour les select
        element.value = value !== null && value !== undefined ? String(value) : '';
      } else {
        const input = element as HTMLInputElement;
        switch (input.type) {
          case 'checkbox':
            input.checked = Boolean(value);
            break;
          case 'number':
            input.value = value !== null && value !== undefined ? String(value) : '';
            break;
          case 'date': {
            // Date LOCALE : `toISOString()` donnerait la date UTC, donc le lendemain a 22 h ici.
            const jour = value instanceof Date ? value : value ? new Date(value) : null;
            if (jour && !isNaN(jour.getTime())) {
              const pad = (n: number): string => n.toString().padStart(2, '0');
              input.value = `${jour.getFullYear()}-${pad(jour.getMonth() + 1)}-${pad(jour.getDate())}`;
            } else {
              input.value = '';
            }
            break;
          }
          case 'datetime-local':
            if (value instanceof Date) {


              const pad = (n: number): string => n.toString().padStart(2, '0');

              input.value = `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`
                + `T${pad(value.getHours())}:${pad(value.getMinutes())}`;

            } else if (value) {
              const pad = (n: number): string => n.toString().padStart(2, '0');
              const newDate = new Date(value);
              input.value = `${newDate.getFullYear()}-${pad(newDate.getMonth() + 1)}-${pad(newDate.getDate())}`
                + `T${pad(newDate.getHours())}:${pad(newDate.getMinutes())}`;
            } else {
              input.value = '';
            }
            break;
          default:
            input.value = value !== null && value !== undefined ? String(value) : '';
        }
      }
    }

    // N'ajouter le listener qu'une seule fois en utilisant une propriété custom
    if (!(element as any).__matwinsBound) {
      (element as any).__matwinsBound = true;

      if (componentInstance && componentInstance[eventName]) {
        componentInstance[eventName].subscribe((value: any) => {
          this.ngZone.run(() => {
            this.updateEditingEntity(fieldName, value);
          });
        });
      }

      element.addEventListener('input', (event: Event) => {
        const target = event.target as HTMLInputElement;
        let typedValue: string | number | boolean | Date | null;

        if (target.tagName === 'SELECT') {
          typedValue = target.value;
        } else {
          switch (target.type) {
            case 'number':
              typedValue = target.value === '' ? null : Number(target.value);
              break;
            case 'checkbox':
              typedValue = target.checked;
              break;
            case 'date': {
              // `new Date("2026-10-01")` = minuit UTC ; on veut minuit LOCAL.
              const [a, m, j] = target.value.split('-').map(Number);
              typedValue = target.value ? new Date(a, m - 1, j) : null;
              break;
            }
            case 'datetime-local':
              typedValue = target.value ? new Date(target.value) : null;
              break;
            default:
              typedValue = target.value;
          }
        }

        this.ngZone.run(() => {
          this.updateEditingEntity(fieldName, typedValue);
        });
      });

      this.registerViewModelProperty(fieldName);
    }
  }

  registerViewModelProperty(property: string): void {
    if (!this.viewModelProperties.includes(property)) {
      this.viewModelProperties.push(property);
      //console.log(' Auto-registered:', property);
    }
  }

  hasValidationErrors(propertyName: string): boolean {
    const errors = this.validationErrors();
    return !!(errors[propertyName] && errors[propertyName].length > 0);
  }

  getValidationErrors(propertyName: string): string[] {
    const errors = this.validationErrors();
    return errors[propertyName] || [];
  }

  validateForm(): boolean {
    const currentEntity = this.editingEntity();
    if (!currentEntity) return false;
    // const metaData = Account.getMetaData();
    // const result = this.validationService.validateEntity(currentEntity, metaData);
    // let allErrors: ValidationState = {};

    // result.errors.forEach(element => {


    //   // const errors = this.validationErrors();
    //   // if (errors[element.property] && errors[element.property].length > 0) {
    //   allErrors[element.property] = [element.message];
    //   // }
    // });

    // this.validationErrors.set(allErrors);

    // return result.isValid;

    const formPropsToValidate = this.viewModelProperties.length > 0
      ? this.viewModelProperties
      : this.getAllPropertyNames(this.entityMetaData, '', true);

    let allErrors: ValidationState = {};

    formPropsToValidate.forEach(propName => {
      const propValue = this.getPropertyValue(currentEntity, propName);
      this.validateProperty(propName, propValue);
      const errors = this.validationErrors();
      if (errors[propName] && errors[propName].length > 0) {
        allErrors[propName] = errors[propName];
      }
    });

    this.validationErrors.set(allErrors);
    return Object.keys(allErrors).length === 0;
  }

  private getAllPropertyNames(metaData: IEntityMetaData, prefix = '', skipKeyProprety: boolean = false): string[] {
    let propertyNames: string[] = [];

    for (const propName in metaData.PropretyDescriptors) {
      const propDescriptor = metaData.PropretyDescriptors[propName];

      if (skipKeyProprety && propDescriptor.PropretyAttributes.indexOf(IPropretyAttribute.KeyProperty) > -1) {
        continue;
      }
      const currentPath = prefix ? `${prefix}.${propName}` : propName;

      if (propDescriptor.TargetTypeMetaData) {
        propertyNames.push(...this.getAllPropertyNames(propDescriptor.TargetTypeMetaData, currentPath));
      } else {
        propertyNames.push(currentPath);
      }
    }

    return propertyNames;
  }

  protected getPropertyValue(entity: any, propertyName: string): any {
    return propertyName.split('.').reduce((o, p) => (o ? o[p] : null), entity);
  }

  async performSaveImageLogo(): Promise<any> {
    if (this.currentImage) {
      return this.uploadFile(this.currentImage, this.editingEntity()?.id ?? '', this.imageRelationName);

    }
  }

  protected async uploadFile(file: File, entityId: string, relationName: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entityId', entityId);
    formData.append('relationName', relationName);
    const result = await this.dataService.execute<any>('Post', 'upload', formData);
    return result;
  }

  async save(): Promise<T | void | null> {
    let isValid = this.validateForm()
    if (!isValid) {
      let formErrors = this.validationErrors();
      console.error('Formulaire invalide', formErrors);
      return;
    }
    try {
      const entity = this.editingEntity();

      if (!entity) return;


      // Vérifier les conflits
      const conflicts: string[] = [];

      for (const prop of entity._modifiedProperties) {
        const original = entity._changeMap?.get(prop)?.old;
        const current = (entity as any)[prop];
        const isUndo = this.isUndoActive(prop);
        if (!isUndo && original !== current) {
          conflicts.push(prop);
        }
      }


      const name = entity.GetType().EntityName;
      const camelEntityName = name.charAt(0).toLowerCase() + name.slice(1);

      let translatedPropNames = conflicts.map((conflict) => { return this.translationService.instant(`entities.${camelEntityName}.propreties.${conflict}`); });

      if (conflicts.length > 0) {
        const confirm = await this.alertModalService?.showAlert({
          title: this.translationService.instant('vues.alert-modals.conflict-found.title'),
          message: this.translationService.instant(`vues.alert-modals.conflict-found.message`, { otherUser: `${entity._modifiedBy}`, modifiedFields: translatedPropNames.join(', ') }),
          messageClasses: 'font-bold',                                                  // optionnel
          yesButtonText: this.translationService.instant('buttons.yes'),                // optionnel
          noButtonText: this.translationService.instant('buttons.no'),                  // optionnel
          Skin: this.Skin // On lui passe le skin courant pour uniformiser le UX
        });
        if (confirm !== AlertModalResult.Yes) return;
      }

      this.loaderService.show();
      try {
        if (this.isNew()) {

          // Create
          const newEntityItem = await this.dataService.create(this.editingEntity()!, this.getComponentId());
          this.entityUpdated.emit(newEntityItem);

          newEntityItem._entityState = EntityState.Unchanged;
          newEntityItem._modifiedProperties?.clear();
          newEntityItem._changeMap?.clear();
          newEntityItem._undoState?.clear();

          this.editingEntity.set(newEntityItem);
          const imageResult = await this.performSaveImageLogo();

          this.isEditMode.set(true);
          this.isNew.set(false);
          return newEntityItem;

        } else {

          // Update 
          await this.dataService.update(this.editingEntity()!.id, this.editingEntity()!, this.getComponentId());
          const imageResult = await this.performSaveImageLogo();

          if (this.EntityInstance) {
            this.parseToEntityInstance();

            this.EntityInstance._entityState = EntityState.Unchanged;
            this.EntityInstance._modifiedProperties?.clear();
            this.EntityInstance._changeMap?.clear();
            this.EntityInstance._undoState?.clear();

            this.entityUpdated.emit(this.EntityInstance);
            this.isEditMode.set(true);
            return this.EntityInstance;
          }
        }
        this.isEditMode.set(true);

      } catch (error) {
        console.error('Save error', error);
        return null;
      }
    }
    finally {
      this.loaderService.hide();
    }
  }


  private hydrateEntity(data: any): T {
    const instance = this.getEmptyEntity();
    Object.assign(instance, data);
    return instance;
  }

  abstract parseToEntityInstance(): void;

  resetEditState(): void {
    this.isEditMode.set(false);
    this.editingEntity.set(null);
    this.validationErrors.set({});
  }

  abstract getEmptyEntity(): T;

  onFileSelected(event: Event, relationName: string): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      this.currentImage = file;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.imageUrl = e.target?.result as string;

      };
      reader.readAsDataURL(file);
    }
  }

  resetImage(): void {
    this.imageUrl = null;
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  updateEditingEntity(property: string, value: any) {
    this.editingEntity.update(db => {
      if (db) {
        const newDb = Object.assign(this.getEmptyEntity(), db);
        const propertyParts = property.split('.');
        let currentObject: any = newDb;

        for (let i = 0; i < propertyParts.length - 1; i++) {
          const part = propertyParts[i];
          if (!currentObject[part]) {
            currentObject[part] = {};
          }
          currentObject = currentObject[part];
        }

        currentObject[propertyParts[propertyParts.length - 1]] = value;

        this.validateProperty(property, value);

        return newDb;
      }
      return db;
    });
  }

  // partie GESTION des WS-PushBack changed propreties

  public hasWebSocketChanges = computed(() => {
    this.refreshTrigger();
    const entity = this.editingEntity();
    return entity && entity._entityState === EntityState.Modified && entity._modifiedProperties.size > 0;
  });

  // Nombre total de propriétés modifiées via WebSocket
  public totalModifiedCount = computed(() => {
    this.refreshTrigger(); // dépendance pour forcer le recalcul
    return this.editingEntity()?._modifiedProperties?.size ?? 0;
  });

  // Obtenir l'état Undo/Redo pour une propriété
  public isUndoActive(property: string): boolean {
    const entity = this.editingEntity();
    return entity?._undoState?.get(property) ?? false;
  }

  // Basculer l'état Undo/Redo pour une propriété
  public toggleUndo(property: string): void {
    const entity = this.editingEntity();
    if (!entity) return;
    if (!entity._undoState) entity._undoState = new Map<string, boolean>();
    const current = entity._undoState.get(property) ?? false;
    entity._undoState.set(property, !current);

    const change = entity._changeMap?.get(property);
    if (!change) return;

    if (!current) {
      // Undo : revenir à l'ancienne valeur
      this.updateEditingEntity(property, change.old);
    } else {
      // Redo : revenir à la nouvelle valeur
      this.updateEditingEntity(property, change.new);
    }

    // Forcer le rafraîchissement
    this.refreshTrigger.update(v => v + 1);
  }

  // Récupérer les informations de modification pour une propriété
  public getModificationInfo(property: string): { modifiedBy?: string; modifiedAt?: Date; oldValue?: any; newValue?: any } | null {
    const entity = this.editingEntity();

    const change = entity?._changeMap?.get(property);

    if (!entity || !entity._modifiedProperties.has(property)) return null;
    return {
      modifiedBy: entity._modifiedBy,
      modifiedAt: entity._modifiedAt,
      oldValue: change?.old,
      newValue: change?.new
    };
  }

  public getModifiedFieldIndex(property: string): number {
    const entity = this.editingEntity();
    if (!entity) return 0;
    const modifiedArray = Array.from(entity._modifiedProperties);
    const index = modifiedArray.indexOf(property);
    return index + 1;
  }

  public propretyHasChanged(propretyName: string): boolean {
    let result = this.editingEntity()?._modifiedProperties?.has(propretyName);
    return result || false;
  }

  onEntityChanged(): void {
    // Forcer la mise à jour du signal pour recalculer les computed
    this.refreshTrigger.update(v => v + 1);
  }

  public triggerFlash(property: string): void {
    this.flashingProperty.set(property);
    setTimeout(() => {
      this.flashingProperty.set(null);
    }, 1000);
  }

  // Méthode appelée pour maintenir la continuité avec le cache
  // lors des changement live via WS-PushBack 
  public updateFromCache(entity: T): void {
    const current = this.editingEntity();
    if (current && current.id === entity.id) {
      // Créer une nouvelle instance avec les données du cache
      const newEntity = this.getEmptyEntity();
      Object.assign(newEntity, entity);
      this.editingEntity.set(newEntity);
      this.onEntityChanged(); // force le recalcul des computed
    }
  }
}
